import React, { useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import styles from './JournalView.module.scss'
import UserContext from '../context/UserContext'
import {
  GET_JOURNAL_ENTRIES_QUERY,
  GET_REFLECTIONS_QUERY,
  DELETE_JOURNAL_ENTRY_MUTATION,
  ADD_REFLECTION_MUTATION,
} from '../query/gqlQueries'
import { speak } from '../speech/speak'
import voices from '../speech/voices'

import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'

const MOOD_EMOJI = { 1: '😞', 2: '😕', 3: '😐', 4: '🙂', 5: '😄' }

const MODELS = ['gemma4:26b', 'gemma3:27b']

// Guidance modes — each frames the same journal history differently.
const MODES = [
  { id: 'daily', label: 'How am I doing?', ask: 'Give me a short, warm reflection on how I seem to be doing lately. Notice one thing going well and gently name one thing I might tend to.' },
  { id: 'low', label: "I'm feeling low", ask: "I'm feeling low right now. Respond with warmth and grounding. Remind me of what has helped before in my own entries, and suggest one small, kind next step." },
  { id: 'patterns', label: 'Spot my patterns', ask: 'Looking across my entries, gently point out one recurring pattern of behaviour or feeling that may not be serving me long-term, and one small experiment to shift it.' },
]

// Neo4j returns DateTime as an object {year, month, day, hour, minute, ...}
// (no custom scalar serializer). Also tolerate ISO strings just in case.
const formatDateTime = (dt) => {
  if (!dt) return ''
  try {
    if (typeof dt === 'object' && dt.year != null) {
      const y = dt.year.low ?? dt.year
      const mo = (dt.month?.low ?? dt.month) || 1
      const d = (dt.day?.low ?? dt.day) || 1
      const h = (dt.hour?.low ?? dt.hour) || 0
      const mi = (dt.minute?.low ?? dt.minute) || 0
      return new Date(y, mo - 1, d, h, mi).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    }
    const parsed = new Date(dt)
    if (isNaN(parsed.getTime())) return ''
    return parsed.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export default function JournalView({ setIsVisible, voiceSpeed, isVisible }) {
  const { username } = useContext(UserContext)

  const { data, loading, error, refetch } = useQuery(GET_JOURNAL_ENTRIES_QUERY, {
    variables: { username, limit: 300 },
    skip: !username,
  })
  const { data: reflData, refetch: refetchReflections } = useQuery(GET_REFLECTIONS_QUERY, {
    variables: { username, limit: 50 },
    skip: !username,
  })

  const [deleteEntry] = useMutation(DELETE_JOURNAL_ENTRY_MUTATION)
  const [addReflection] = useMutation(ADD_REFLECTION_MUTATION)

  const [selectedModel, setSelectedModel] = useState(MODELS[0])
  // Full back-and-forth. Each item: {role, content, label?}. `label` lets a
  // starter button show a short title while the model receives the full ask.
  const [conversation, setConversation] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const [aiError, setAiError] = useState('')
  const [isRecording, setIsRecording] = useState(false)

  // Time-of-day "warm suggestions" — regenerated each time the view opens.
  const [suggestions, setSuggestions] = useState([])
  const [suggLoading, setSuggLoading] = useState(false)
  const [suggError, setSuggError] = useState('')

  const chatLogRef = useRef(null)
  const recognitionRef = useRef(null)
  const suggestControllerRef = useRef(null)
  const prevVisibleRef = useRef(false)
  const pendingOpenRef = useRef(false)

  const entries = useMemo(() => data?.getJournalEntries || [], [data])
  const reflections = reflData?.getReflections || []

  const handleDelete = async (identity, e) => {
    e.stopPropagation()
    if (!window.confirm('Delete this journal entry?')) return
    try {
      await deleteEntry({ variables: { identity } })
      refetch()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  // Recent entries (most recent first, capped) formatted for the model. Carried
  // in the system message so every turn of the conversation stays grounded in
  // the journal, not just the opening one.
  const systemContent = useCallback(() => {
    const context = entries.slice(0, 40).map((en) => {
      const p = en.properties
      const when = formatDateTime(p.createdAt)
      const moodStr = p.mood ? ` (mood ${p.mood}/5)` : ''
      const tagStr = (p.tags && p.tags.length) ? ` [${p.tags.join(', ')}]` : ''
      return `- ${when}${moodStr}${tagStr}: ${p.body}`
    }).join('\n')
    return `You are a warm, supportive journaling companion — like a wise, caring friend. You are NOT a doctor or therapist and must not diagnose or prescribe. Keep replies concise (a few short sentences), gentle, specific to what the person actually wrote, and never preachy. This is an ongoing conversation — answer their follow-up questions and stay warm and grounded. If they mention being in crisis or wanting to harm themselves, gently and clearly encourage them to reach out to a crisis line or someone they trust.

Here are their recent journal entries (most recent first):

${context}`
  }, [entries])

  const callModel = useCallback(async (apiMessages) => {
    const serverUrl = process.env.REACT_APP_OLLAMA_URI
    const apiKey = process.env.REACT_APP_OLLAMA_KEY
    const res = await fetch(`${serverUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      body: JSON.stringify({
        model: selectedModel,
        messages: apiMessages,
        stream: false, keep_alive: '30m',
        options: { temperature: 0.7 },
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = await res.json()
    const text = json.message?.content?.trim() || ''
    if (!text) throw new Error('Empty response')
    return text
  }, [selectedModel])

  const humanError = (err) =>
    (err.message?.includes('Failed to fetch') || err.message?.includes('Load failed'))
      ? 'Could not reach the Mac Studio. Is the tunnel up?'
      : (err.message || 'Something went wrong.')

  // A starter button opens a fresh conversation and persists the first reply as
  // a Reflection (the meaningful "guidance" artifact worth keeping).
  const startConversation = useCallback(async (mode) => {
    if (entries.length === 0) { setAiError('Write a few journal entries first.'); return }
    const modeDef = MODES.find((m) => m.id === mode) || MODES[0]
    setAiError('')
    setThinking(true)
    const opening = [{ role: 'user', content: modeDef.ask, label: modeDef.label }]
    setConversation(opening)
    try {
      const text = await callModel([
        { role: 'system', content: systemContent() },
        { role: 'user', content: modeDef.ask },
      ])
      setConversation([...opening, { role: 'assistant', content: text }])
      try {
        await addReflection({ variables: { input: { username, text, model: selectedModel, mode } } })
        refetchReflections()
      } catch (saveErr) {
        console.warn('Could not save reflection:', saveErr)
      }
    } catch (err) {
      console.error('AI advice failed:', err)
      setAiError(humanError(err))
      setConversation([]) // drop the dangling opening bubble on failure
    } finally {
      setThinking(false)
    }
  }, [entries, callModel, systemContent, addReflection, refetchReflections, username, selectedModel])

  const sendReply = useCallback(async () => {
    const text = chatInput.trim()
    if (!text || thinking || conversation.length === 0) return
    setAiError('')
    const next = [...conversation, { role: 'user', content: text }]
    setConversation(next)
    setChatInput('')
    setThinking(true)
    try {
      const apiMessages = [
        { role: 'system', content: systemContent() },
        ...next.map(({ role, content }) => ({ role, content })),
      ]
      const reply = await callModel(apiMessages)
      setConversation([...next, { role: 'assistant', content: reply }])
    } catch (err) {
      console.error('Reply failed:', err)
      setAiError(humanError(err))
    } finally {
      setThinking(false)
    }
  }, [chatInput, thinking, conversation, systemContent, callModel])

  // Dictate a reply — transcript only, appended to the input box.
  const toggleReplyMic = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) { setAiError('Speech recognition is not supported in this browser.'); return }
    if (isRecording) { recognitionRef.current?.stop(); return }
    try { window.speechSynthesis?.cancel() } catch { /* fine */ }
    const rec = new SpeechRecognition()
    rec.lang = 'en-US'
    rec.interimResults = false
    rec.onstart = () => setIsRecording(true)
    rec.onend = () => setIsRecording(false)
    rec.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      setChatInput((prev) => (prev.trim() ? prev.trim() + ' ' : '') + transcript)
    }
    recognitionRef.current = rec
    try { rec.start() } catch { /* fine */ }
  }

  // Auto-scroll the chat as it grows.
  useEffect(() => {
    if (chatLogRef.current) chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight
  }, [conversation, thinking])

  useEffect(() => () => { try { recognitionRef.current?.abort() } catch { /* fine */ } }, [])

  const speakAdvice = (text) => speak(text, voices['eng'], voiceSpeed)

  const hour = new Date().getHours()
  const greeting =
    hour < 5 ? 'Still up?' : hour < 11 ? 'Good morning' : hour < 14 ? 'Midday check-in'
      : hour < 18 ? 'Good afternoon' : hour < 22 ? 'Good evening' : 'Winding down'

  // ── Warm, time-of-day suggestions ────────────────────────────────────────
  const dayPart = (h) =>
    h < 5 ? 'late night' : h < 11 ? 'morning' : h < 14 ? 'midday' : h < 18 ? 'afternoon' : h < 22 ? 'evening' : 'late night'

  const fetchSuggestions = useCallback(async () => {
    const serverUrl = process.env.REACT_APP_OLLAMA_URI
    const apiKey = process.env.REACT_APP_OLLAMA_KEY
    if (!serverUrl) return
    if (suggestControllerRef.current) suggestControllerRef.current.abort()
    suggestControllerRef.current = new AbortController()
    setSuggLoading(true)
    setSuggError('')

    const now = new Date()
    const part = dayPart(now.getHours())
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

    const context = entries.slice(0, 25).map((en) => {
      const p = en.properties
      const moodStr = p.mood ? ` (mood ${p.mood}/5)` : ''
      const tagStr = (p.tags && p.tags.length) ? ` [${p.tags.join(', ')}]` : ''
      return `- ${formatDateTime(p.createdAt)}${moodStr}${tagStr}: ${p.body}`
    }).join('\n') || 'No entries yet.'
    const reflectionStr = reflections.slice(0, 3).map((r) => `- ${r.properties.text}`).join('\n') || 'None yet.'

    const prompt = `You are a warm, caring wellbeing companion — like a kind friend who wants the best for this person. It is ${part} (${timeStr} local time).

Their recent journal entries (most recent first):
${context}

Recent things you've reflected back to them:
${reflectionStr}

Give EXACTLY 3 short, warm, specific suggestions for right now that would help them feel better and live well. Tailor them to the time of day and to what they actually wrote. Favour gentle, healthy, life-affirming nudges — e.g. eat something nourishing, step into the sunlight, take a 10-minute walk, breathe for a minute, message someone they care about, choose a kinder alternative to an unhealthy habit, get outside among people rather than staying in and avoiding life. Each suggestion must be ONE friendly, encouraging sentence, written directly to them (imperative, warm, never preachy or clinical).

Respond ONLY with valid JSON in exactly this form:
{"suggestions": ["first suggestion", "second suggestion", "third suggestion"]}`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        signal: suggestControllerRef.current.signal,
        body: JSON.stringify({
          model: selectedModel,
          prompt,
          stream: false, keep_alive: '30m',
          format: 'json',
          options: { temperature: 0.7, num_predict: 260 },
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const raw = (data.response || '').replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(raw)
      const list = (parsed.suggestions || [])
        .filter((s) => typeof s === 'string' && s.trim())
        .map((s) => s.trim())
        .slice(0, 3)
      if (list.length === 0) throw new Error('No suggestions returned')
      setSuggestions(list)
    } catch (err) {
      if (err.name === 'AbortError') return
      console.error('Suggestions failed:', err)
      setSuggError(err.message?.includes('Failed to fetch') || err.message?.includes('Load failed')
        ? 'Could not reach the Mac Studio for suggestions.'
        : 'Could not load suggestions right now.')
    } finally {
      setSuggLoading(false)
    }
  }, [entries, reflections, selectedModel])

  // Regenerate on every open (the view stays mounted; visibility just toggles).
  // If opened before the entries query settles, wait for it, then fetch.
  useEffect(() => {
    if (isVisible && !prevVisibleRef.current) pendingOpenRef.current = true
    prevVisibleRef.current = isVisible
    if (pendingOpenRef.current && isVisible && !loading) {
      pendingOpenRef.current = false
      fetchSuggestions()
    }
  }, [isVisible, loading, fetchSuggestions])

  useEffect(() => () => { try { suggestControllerRef.current?.abort() } catch { /* fine */ } }, [])

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}>
        <CloseIcon />
      </button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div>
            <div className={styles.label}>Private · on your machines</div>
            <h1 className={styles.title}>Journal</h1>
          </div>
          <button className={styles.iconBtn} onClick={() => refetch()} title="Refresh">
            <RefreshIcon fontSize="small" />
          </button>
        </header>

        {(suggLoading || suggestions.length > 0 || suggError) && (
          <div className={styles.suggestCard}>
            <div className={styles.suggestHeader}>
              <div>
                <div className={styles.suggestGreeting}>{greeting}</div>
                <div className={styles.suggestSub}>A few warm things for right now</div>
              </div>
              <button className={styles.iconBtn} onClick={fetchSuggestions} disabled={suggLoading} title="New suggestions">
                <RefreshIcon fontSize="small" />
              </button>
            </div>
            {suggLoading ? (
              <div className={styles.suggestLoading}>Thinking of a few warm suggestions for you… this can take a moment.</div>
            ) : suggError ? (
              <div className={styles.aiError}>{suggError}</div>
            ) : (
              <div className={styles.suggestList}>
                {suggestions.map((s, i) => (
                  <div key={i} className={styles.suggestItem}>
                    <span className={styles.suggestDot}>♡</span>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className={styles.layout}>
          {/* ── Entries timeline ─────────────────────────────── */}
          <div className={styles.entriesPane}>
            <div className={styles.paneTitle}>Entries {entries.length > 0 && <span>{entries.length}</span>}</div>
            {loading ? (
              <div className={styles.empty}>Loading…</div>
            ) : error ? (
              <div className={styles.empty}>Error: {error.message}</div>
            ) : entries.length === 0 ? (
              <div className={styles.empty}>No entries yet. Add one from the + button (Journal Entry).</div>
            ) : (
              <div className={styles.entryList}>
                {entries.map((en) => {
                  const p = en.properties
                  return (
                    <div key={en.identity} className={styles.entryCard}>
                      <div className={styles.entryTop}>
                        <span className={styles.entryDate}>{formatDateTime(p.createdAt)}</span>
                        {p.mood != null && <span className={styles.entryMood}>{MOOD_EMOJI[p.mood] || ''}</span>}
                        {p.source === 'voice' && <span className={styles.voiceTag}>🎙 voice</span>}
                        <button className={styles.deleteBtn} onClick={(e) => handleDelete(en.identity, e)} title="Delete">
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                      {p.title && <div className={styles.entryTitle}>{p.title}</div>}
                      <div className={styles.entryBody}>{p.body}</div>
                      {p.tags && p.tags.length > 0 && (
                        <div className={styles.tagRow}>
                          {p.tags.map((t, i) => <span key={i} className={styles.tag}>{t}</span>)}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── AI companion ─────────────────────────────────── */}
          <div className={styles.aiPane}>
            <div className={styles.paneTitle}><AutoAwesomeIcon style={{ fontSize: 16 }} /> Reflect with your Journal AI companion</div>

            <div className={styles.modeButtons}>
              {MODES.map((m) => (
                <button key={m.id} className={styles.modeBtn} onClick={() => startConversation(m.id)} disabled={thinking}>
                  {m.label}
                </button>
              ))}
            </div>

            <select className={styles.modelSelect} value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
              {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>

            {thinking && conversation.length === 0 && <div className={styles.thinking}>Reflecting on your entries…</div>}
            {aiError && <div className={styles.aiError}>{aiError}</div>}

            {conversation.length > 0 && (
              <>
                <div className={styles.chatLog} ref={chatLogRef}>
                  {conversation.map((m, i) => (
                    <div key={i} className={`${styles.msg} ${m.role === 'user' ? styles.msgUser : styles.msgAssistant}`}>
                      <div className={styles.msgText}>{m.label ?? m.content}</div>
                      {m.role === 'assistant' && (
                        <button className={styles.msgSpeak} onClick={() => speakAdvice(m.content)} title="Read aloud">
                          <VolumeUpIcon style={{ fontSize: 14 }} />
                        </button>
                      )}
                    </div>
                  ))}
                  {thinking && (
                    <div className={`${styles.msg} ${styles.msgAssistant}`}>
                      <div className={styles.typing}><span /><span /><span /></div>
                    </div>
                  )}
                </div>

                <div className={styles.replyRow}>
                  <button
                    className={`${styles.replyIcon} ${isRecording ? styles.replyIconRec : ''}`}
                    onClick={toggleReplyMic}
                    title="Dictate a reply"
                  >
                    {isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                  </button>
                  <textarea
                    className={styles.replyInput}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply() } }}
                    placeholder="Reply to continue the conversation…"
                    rows={1}
                  />
                  <button className={styles.replySend} onClick={sendReply} disabled={!chatInput.trim() || thinking}>
                    Send
                  </button>
                </div>
              </>
            )}

            {reflections.length > 0 && (
              <div className={styles.historyBlock}>
                <div className={styles.historyTitle}>Past reflections</div>
                {reflections.map((r) => (
                  <div key={r.identity} className={styles.historyItem}>
                    <div className={styles.historyMeta}>
                      {formatDateTime(r.properties.createdAt)}
                      {r.properties.mode && <span className={styles.historyMode}>{r.properties.mode}</span>}
                    </div>
                    <div className={styles.historyText}>{r.properties.text}</div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.disclaimer}>
              A supportive companion, not a therapist. If you're in crisis, please reach out to a crisis line or someone you trust.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
